import { yearsSinceFounding } from "@/lib/school-age";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Volume2, VolumeX } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useRef, useState } from "react";
import heroImage from "@/assets/school-building-holiday.webp";

const isVideoUrl = (url: string): boolean =>
  /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url);

interface HeroData {
  url: string;
  title: string | null;
  showTitle: boolean;
}

const getCurrentHeroImage = async (): Promise<HeroData | null> => {
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();
  const currentDateStr = `${String(currentMonth).padStart(2, "0")}-${String(currentDay).padStart(2, "0")}`;

  // Fetch all schedules, ordered by priority (specific dates first)
  const {
    data: schedules,
    error: schedulesError
  } = await supabase.from("hero_image_schedules").select("*").order("is_specific_date", {
    ascending: false
  }).order("priority", {
    ascending: false
  }).order("start_date", {
    ascending: true
  });
  if (schedulesError) {
    console.error("Error fetching hero image schedules:", schedulesError);
    return null;
  }
  if (!schedules || schedules.length === 0) {
    // No schedules, check for default image
    const {
      data: settings,
      error: settingsError
    } = await supabase.from("hero_image_settings").select("default_image_url").single();
    if (settingsError || !settings?.default_image_url) {
      return null; // Will use static fallback
    }
    const {
      data: urlData
    } = supabase.storage.from("hero-images").getPublicUrl(settings.default_image_url);
    return { url: urlData.publicUrl, title: null, showTitle: false };
  }

  // Find matching schedule (schedules are already sorted by priority)
  const matchingSchedule = schedules.find(schedule => {
    const startParts = schedule.start_date.split("-");
    const endParts = schedule.end_date.split("-");
    const startMonthDay = `${startParts[1]}-${startParts[2]}`;
    const endMonthDay = `${endParts[1]}-${endParts[2]}`;

    // Handle year wraparound (e.g., Dec 20 - Jan 15)
    let matches = false;
    if (startMonthDay <= endMonthDay) {
      matches = currentDateStr >= startMonthDay && currentDateStr <= endMonthDay;
    } else {
      matches = currentDateStr >= startMonthDay || currentDateStr <= endMonthDay;
    }
    return matches;
  });
  if (matchingSchedule) {
    const {
      data: urlData
    } = supabase.storage.from("hero-images").getPublicUrl(matchingSchedule.image_url);
    return {
      url: urlData.publicUrl,
      title: matchingSchedule.name ?? null,
      showTitle: matchingSchedule.show_title ?? false,
    };
  }

  // No matching schedule, fall back to default image
  const {
    data: settings,
    error: settingsError
  } = await supabase.from("hero_image_settings").select("default_image_url").single();
  if (settingsError || !settings?.default_image_url) {
    return null; // Will use static fallback
  }
  const {
    data: urlData
  } = supabase.storage.from("hero-images").getPublicUrl(settings.default_image_url);
  return { url: urlData.publicUrl, title: null, showTitle: false };
};
const Hero = () => {
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const heroRetryRef = useRef(0);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Invalidate hero image cache on auth state change to ensure fresh data
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({
        queryKey: ["currentHeroImage"]
      });
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);
  const {
    data: heroImageUrl,
    isLoading
  } = useQuery({
    queryKey: ["currentHeroImage"],
    queryFn: getCurrentHeroImage,
    staleTime: 5 * 60 * 1000,
    // 5 minutes
    gcTime: 30 * 60 * 1000 // 30 minutes
  });

  // Use scheduled image if available, otherwise fall back to static image
  const imageSrc = heroImageUrl?.url || heroImage;
  const heroTitle = heroImageUrl?.showTitle ? heroImageUrl.title : null;
  const imageAlt = heroImageUrl ? "The Children's Hour Academy building" : "The Children's Hour Academy building decorated for the holidays";
  return <div className="relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <Card className="rounded-3xl shadow-spark-frame p-8 md:p-12 border-0 bg-[#fffdd0]">
            <div className="grid md:grid-cols-[1fr_1.4fr] gap-8 items-start">
              <div className="animate-fade-in-up space-y-6">
                <div className="space-y-4 text-base text-[#2C003E] leading-relaxed">
                  <p>At The Children's Hour Academy, we nurture young hearts, treasure curiosity, and foster learning in the rhythm of everyday moments. Here, children find a place where they are truly seen, valued, and encouraged to discover the joy of their abilities.</p>
                  
                  <p>For over {yearsSinceFounding()} years, our school has been a home for families seeking more than childcare, a place where relationships come first, guided by trust, respect, and the belief that each child's journey is unique.</p>
                  
                  <p>
                    Our programs blend the Creative Curriculum and TS GOLD® assessment tools with a rich S.T.E.A.M. focus, ensuring that learning is both meaningful and joyful. As a SPARK 5-Star Quality Program, Oregon's highest quality rating, we uphold excellence in early childhood education while keeping the heart of what matters most, the children.
                  </p>
                  
                  <p>
                    Come see for yourself why The Children's Hour Academy is a place where children thrive, families feel at home, and a love for learning blooms.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link to="/schedule-tour">
                    <Button variant="hero" size="lg" className="group">
                      Schedule a Tour
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
              
              <div className="relative animate-fade-in">
                <div className="absolute inset-0 bg-gradient-hero opacity-20 blur-3xl rounded-full"></div>
                {!isLoading && (isVideoUrl(imageSrc) ? (
                  <div className="relative">
                    <video
                      ref={videoRef}
                      src={imageSrc}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="relative rounded-2xl shadow-large w-full h-auto"
                      width={1200}
                      height={900}
                      onError={e => {
                        const target = e.target as HTMLVideoElement;
                        if (target.src !== heroImage) {
                          target.style.display = "none";
                          const img = document.createElement("img");
                          img.src = heroImage;
                          img.alt = imageAlt;
                          img.className = target.className;
                          img.width = 1200;
                          img.height = 900;
                          target.parentElement?.appendChild(img);
                        }
                      }}
                    />
                    <button
                      onClick={toggleMute}
                      className="absolute bottom-3 right-3 bg-[#2E7DB2] hover:bg-[#2E7DB2]/80 text-white rounded-full p-2 transition-colors ring-2 ring-white"
                      aria-label={isMuted ? "Unmute video" : "Mute video"}
                    >
                      {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                    </button>
                  </div>
                ) : (
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    className="relative rounded-2xl shadow-large w-full h-auto"
                    width={1200}
                    height={900}
                    {...({ fetchpriority: "high" } as Record<string, string>)}
                    decoding="async"
                    crossOrigin="anonymous"
                    onError={e => {
                    const target = e.target as HTMLImageElement;
                    const scheduledUrl = heroImageUrl?.url;
                    // Cellular connections drop hero downloads mid-stream;
                    // retry before giving up on the scheduled image. The
                    // retry param forces a fresh request past any
                    // half-received response.
                    if (scheduledUrl && !target.src.endsWith(heroImage) && heroRetryRef.current < 2) {
                      heroRetryRef.current += 1;
                      const attempt = heroRetryRef.current;
                      window.setTimeout(() => {
                        target.src = `${scheduledUrl}${scheduledUrl.includes("?") ? "&" : "?"}retry=${attempt}`;
                      }, attempt * 1000);
                    } else if (!target.src.endsWith(heroImage)) {
                      target.src = heroImage;
                    }
                  }} />
                ))}
                {heroTitle && (
                  <p className="mt-3 text-center text-lg font-semibold text-black">{heroTitle}</p>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>;
};
export default Hero;